import { Alert, FormGroup, Input, Label } from 'reactstrap';

import DateStrip from 'src/shared/components/ui/DateStrip';
import { todayKey } from 'src/shared/utils/datetime';
import SlotGrid from './SlotGrid';
import { upcomingDays } from './dayChoices';
import type { BookingForm } from './useBookingForm';

interface WhenPickerProps {
  form: BookingForm;
  timeZone?: string;
  /** Owner and manager may book outside the free slots. */
  mayOverride: boolean;
  /** Called when a free slot is tapped, so the wizard can move on. */
  onPicked?: () => void;
}

/** The day, as a strip of the next two weeks, then its free times. */
export default function WhenPicker({ form, timeZone, mayOverride, onPicked }: WhenPickerProps) {
  const today = todayKey(timeZone);

  return (
    <div>
      <DateStrip
        days={upcomingDays(today, form.date)}
        selected={form.date}
        onSelect={form.pickDate}
        label="Día de la cita"
        className="mb-2"
      />
      <FormGroup className="mb-4">
        <Label for="bookDate" className="small text-muted mb-1">
          Otra fecha
        </Label>
        <Input
          id="bookDate"
          type="date"
          value={form.date}
          onChange={(e) => {
            if (e.target.value) form.pickDate(e.target.value);
          }}
        />
      </FormGroup>

      <p className="v-eyebrow mb-2">Horarios libres</p>
      {form.serviceIds.length === 0 ? (
        <div className="text-muted small mb-3">Elige al menos un servicio para ver horarios.</div>
      ) : form.availability.isPending ? (
        <div className="text-muted small mb-3">Buscando horarios…</div>
      ) : form.availability.isError ? (
        <Alert color="danger" role="alert">
          No pudimos cargar los horarios. Inténtalo de nuevo.
        </Alert>
      ) : form.slots.length === 0 ? (
        <Alert color="warning" role="status">
          No queda ningún hueco ese día. Prueba con otra fecha.
        </Alert>
      ) : (
        <div className="mb-3">
          <SlotGrid
            slots={form.slots}
            selected={form.activeSlot}
            onSelect={(slot) => {
              form.pickSlot(slot);
              onPicked?.();
            }}
            timeZone={timeZone}
          />
        </div>
      )}

      {/* The exception path, for the salon that stays late for a regular. */}
      {mayOverride && (
        <details className="v-details">
          <summary>Agendar en otro horario</summary>
          <div className="d-flex flex-wrap align-items-end gap-3 mt-2">
            <FormGroup className="mb-0">
              <Label for="manualTime" className="small">
                Hora
              </Label>
              <Input
                id="manualTime"
                type="time"
                value={form.manualTime}
                onChange={(e) => form.pickManualTime(e.target.value)}
              />
            </FormGroup>
            <FormGroup check className="mb-2">
              <Input
                id="bookForce"
                type="checkbox"
                checked={form.force}
                onChange={(e) => form.setForce(e.target.checked)}
              />
              <Label for="bookForce" check>
                Fuera de horario
              </Label>
            </FormGroup>
          </div>
        </details>
      )}
    </div>
  );
}
